import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { PolygonLayer } from '@deck.gl/layers';
import DemographicsSelector from './DemographicsSelector';
import DemographicsLegend from './DemographicsNavigator';
import fetchDemographicsData from '../services/fetchDemographicsData';
import { AppCTX } from '../data/AppContext';

// Demographics loading overlay component
const DemographicsLoadingOverlay = React.memo(() => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      pointerEvents: 'auto'
    }}
  >
    <div
      style={{
        width: '60px',
        height: '60px',
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderTop: '4px solid #ffffff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
    <div
      style={{
        color: 'white',
        marginTop: '20px',
        fontSize: '16px',
        fontWeight: '500',
        textAlign: 'center'
      }}
    >
      Loading Demographics Data...
    </div>
    <div
      style={{
        color: 'rgba(255, 255, 255, 0.8)',
        marginTop: '8px',
        fontSize: '14px',
        textAlign: 'center'
      }}
    >
      Please wait while we fetch the demographic information
    </div>
  </div>
));

const DemographicsOverlay = ({ 
  isActive,
  areaManagerActive,
  onToggle,
  onLayersChange
}) => {
  // Track re-renders
  const renderCount = useRef(0);
  renderCount.current += 1;
  
  console.log('🔄 DEMOGRAPHICSOVERLAY: Re-render #', renderCount.current, {
    timestamp: new Date().toISOString(),
    props: {
      isActive,
      areaManagerActive,
      hasOnToggle: !!onToggle
    }
  });

  // Get setStatus from AppContext
  const { setStatus } = React.useContext(AppCTX);

  // Internal demographics state - completely isolated
  const [selectedDemographic, setSelectedDemographic] = useState('black_percentage');
  const [demographicsLoading, setDemographicsLoading] = useState(false);
  const [demographicsData, setDemographicsData] = useState(null);
  const [demographicsError, setDemographicsError] = useState(null);
  const [clickedPolygonInfo, setClickedPolygonInfo] = useState(null);
  const [highlightedPolygon, setHighlightedPolygon] = useState(null);
  const [selectedGeoLevel, setSelectedGeoLevel] = useState('nationwide'); // Track current level
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  // Create handleDataFetch function at component level
  const handleDataFetch = useCallback(async (geoLevel = null, areaId = null) => {
    try {
      const data = await fetchDemographicsData(geoLevel, areaId);
      setDemographicsData(data);
    } catch (err) {
      setDemographicsError(err.message);
      
      // Show error message via snackbar
      setStatus({ 
        notificationType: 'error', 
        message: 'Demographics data is currently unavailable. Please try again later.' 
      });
      
      // Deactivate demographics overlay when there's an error
      if (onToggle) {
        onToggle(); // This will deactivate the overlay
      }
    } finally {
      setDemographicsLoading(false);
    }
  }, [setStatus, onToggle]);

  // Fetch demographics data when overlay is active or state level changes
  useEffect(() => {
    if (!isActive) {
      setDemographicsData(null);
      setDemographicsLoading(false);
      setDemographicsError(null);
      setClickedPolygonInfo(null);
      setHighlightedPolygon(null);
      setSelectedGeoLevel('nationwide'); // Reset to nationwide when deactivating
      return;
    }

    // // Only fetch data once when overlay becomes active (but allow refresh on level change)
    // if (demographicsData && selectedGeoLevel === 'nationwide' && isActive && !wasCountyLevel) {
    //   return;
    // }
    
    setDemographicsLoading(true);
    setDemographicsError(null);

    // Determine what to fetch based on selected level
    console.log('🔄 DEMOGRAPHICSOVERLAY: Fetching data for level:', selectedGeoLevel, 'parameter:', selectedAreaId);
    handleDataFetch(selectedGeoLevel, selectedAreaId);
  }, [isActive, selectedGeoLevel, selectedAreaId, handleDataFetch]);
  // Create demographics layers - separated into polygon and label layers for different zoom dependencies
  const polygonLayer = useMemo(() => {
    if (!demographicsData || !demographicsData.features) {
      return null;
    }

    // Pre-process features to flatten MultiPolygons into individual Polygon features
    const flattenedFeatures = demographicsData.features.flatMap(feature => {
      if (feature.geometry.type === 'MultiPolygon') {
        // Create a separate feature for each polygon in the MultiPolygon
        return feature.geometry.coordinates.map((polygonCoords, index) => ({
          ...feature,
          id: `${feature.id || 'feature'}_part_${index}`, // Ensure unique ID
          geometry: {
            type: 'Polygon',
            coordinates: polygonCoords
          }
        }));
      }
      // Keep single polygons as-is
      return [feature];
    });

    console.log('🔄 DEMOGRAPHICSOVERLAY: Feature processing', {
      originalFeatures: demographicsData.features.length,
      flattenedFeatures: flattenedFeatures.length,
      hasMultiPolygons: demographicsData.features.some(f => f.geometry.type === 'MultiPolygon')
    });

    // Create polygon layer
    return new PolygonLayer({
      id: 'demographics-overlay',
      data: flattenedFeatures,
      getPolygon: f => {
        // Now all features are simple Polygons
        return f.geometry.coordinates[0];
      },
      getFillColor: (f) => {
        const value = f.properties?.[selectedDemographic] ?? 0;
        
        // Fixed colors for each demographic type
        let r, g, b;
        if (selectedDemographic === 'hispanic_percentage') {
          r = 255; g = 0; b = 0; // Bright Red
        } else if (selectedDemographic === 'white_percentage') {
          r = 0; g = 150; b = 255; // Bright Blue
        } else if (selectedDemographic === 'black_percentage') {
          r = 150; g = 0; b = 255; // Bright Purple
        } else if (selectedDemographic === 'asian_percentage') {
          r = 0; g = 255; b = 0; // Bright Green
        } else if (selectedDemographic === 'mixed_percentage') {
          r = 255; g = 100; b = 0; // Bright Orange
        } else if (selectedDemographic === 'total_population') {
          r = 255; g = 255; b = 0; // Bright Yellow
        } else {
          r = 0; g = 150; b = 255; // Default bright blue
        }
        
        const percentage = value; // Already a percentage (0-100)
        const alpha = Math.round((percentage / 100) * 255); // Convert to 0-255 range
        
        return [r, g, b, alpha]; // Return RGBA instead of RGB
      },
      pickable: true,
      stroked: true,
      filled: true,
      lineWidthMinPixels: 1,
      getLineColor: [0, 0, 0, 50], // Subtle black borders
      getLineWidth: 1,
      opacity: 0.8,
      autoHighlight: false, // Disable auto highlight to handle it manually
      highlightColor: [255, 255, 0, 180],
      onClick: (info) => {
        setClickedPolygonInfo(info);
        setHighlightedPolygon(info.object);
      },
      updateTriggers: {
        getFillColor: [selectedDemographic]
      }
    });
  }, [demographicsData, selectedDemographic]);

  // Create highlight layer for clicked polygon
  const highlightLayer = useMemo(() => {
    if (!highlightedPolygon) {
      return null;
    }

    return new PolygonLayer({
      id: 'demographics-clicked-highlight',
      data: [highlightedPolygon],
      getPolygon: f => {
        // Handle both original and flattened features
        if (f.geometry.type === 'MultiPolygon') {
          return f.geometry.coordinates[0][0]; // For original MultiPolygon
        }
        return f.geometry.coordinates[0]; // For flattened Polygon
      },
      getFillColor: [255, 255, 0, 100], // Fade out when expiring
      getLineColor: [255, 255, 0, 255], // Fade border when expiring
      getLineWidth: 3, // Thinner border when expiring
      pickable: false,
      stroked: true,
      filled: true,
      opacity: 0.7 // Lower opacity when expiring
    });
  }, [highlightedPolygon]);

  // Combine layers - polygon layer and highlight layer
  const layers = useMemo(() => {
    const resultLayers = [];
    if (polygonLayer) resultLayers.push(polygonLayer);
    if (highlightLayer) resultLayers.push(highlightLayer); // Add highlight layer on top
    return resultLayers;
  }, [polygonLayer, highlightLayer]);

  // Pass layers to parent component
  useEffect(() => {
    if (onLayersChange) {
      onLayersChange(layers);
    }
  }, [layers, onLayersChange, isActive, areaManagerActive]);

  // Clear layers when overlay becomes inactive
  useEffect(() => {
    if (!isActive && onLayersChange) {
      onLayersChange([]);
    }
  }, [isActive, onLayersChange]);

  // Clear layers when area manager becomes active
  useEffect(() => {
    if (areaManagerActive && onLayersChange) {
      onLayersChange([]);
    }
  }, [areaManagerActive, onLayersChange]);

  // Cleanup layers when component unmounts or area manager becomes active
  useEffect(() => {
    return () => {
      if (onLayersChange) {
        onLayersChange([]);
      }
    };
  }, [onLayersChange]);

  // Memoized demographics components props
  const demographicsSelectorProps = useMemo(() => {
    return {
      visible: true,
      selectedDemographic,
      onDemographicChange: setSelectedDemographic,
      onToggle: onToggle, // Use the passed toggle function
      isActive: isActive,
      isLoading: demographicsLoading
    };
  }, [selectedDemographic, isActive, demographicsLoading, onToggle]);

  const demographicsLegendProps = useMemo(() => {
    return {
      visible: true,
      selectedDemographic,
      isActive: isActive,
      isLoading: demographicsLoading,
      clickedPolygonInfo,
      selectedGeoLevel,
      onGoBlockGroupLevel: (countyName) => {
        setSelectedGeoLevel('blockgroup'); // Use simple 'blockgroup' format
        setSelectedAreaId(countyName);
        setClickedPolygonInfo(null);
        setHighlightedPolygon(null);
      },
      onGoCountyLevel: (stateName) => {
        setSelectedGeoLevel('county');
        setSelectedAreaId(stateName);
        setClickedPolygonInfo(null);
        setHighlightedPolygon(null);
      },
      onGoBack: () => {
        if (selectedGeoLevel === 'blockgroup') {
          // If in block group view, go back to county view

          const stateFips = selectedAreaId.split('_')[0];
          setSelectedGeoLevel('county'); // or whatever county level you want
          setSelectedAreaId(stateFips);
          setClickedPolygonInfo(null);
          setHighlightedPolygon(null);
        } else if (selectedGeoLevel === 'county') {
          // If in county view, go back to  
          setSelectedGeoLevel('nationwide');
          setSelectedAreaId(null);
          setClickedPolygonInfo(null);
          setHighlightedPolygon(null);
        } else {
          // Fallback to nationwide
          setSelectedGeoLevel('nationwide');
          setSelectedAreaId(null);
          setClickedPolygonInfo(null);
          setHighlightedPolygon(null);
        }
      }
    };
  }, [selectedDemographic, isActive, demographicsLoading, clickedPolygonInfo, selectedGeoLevel]);

  // Don't render anything if not active or area manager is active
  if (!isActive || areaManagerActive) {
    return null;
  }

  return (
    <>
      {/* Demographics Loading Overlay - Show when loading */}
      {demographicsLoading && (
        <DemographicsLoadingOverlay />
      )}

      {/* Demographics UI Components - Only show when not loading */}
      {!demographicsLoading && (
        <>
          <DemographicsSelector {...demographicsSelectorProps} />
          <DemographicsLegend {...demographicsLegendProps} />
        </>
      )}
    </>
  );
};

export default DemographicsOverlay; 